import React, { FC, useCallback, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ViewStyle, ImageStyle, TouchableOpacity } from "react-native"
import { Button, Screen, Text, AutoImage, useAutoFeederPond, JalaIcon } from "../../../../components"
// import { useStores } from "../../models"
import { color, nativeBaseTheme } from "../../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { View, Modal, useDisclose, Toast } from "native-base"
import { SafeAreaView } from "react-native-safe-area-context"
import { translate } from "../../../../i18n"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import _ from 'lodash'
import { goBack, navigate } from "../../../../navigators"
import { useStores } from "../../../../models"
import { AutoFeederPond } from "../../../../database"
import { delay } from "../../../../utils/delay"
import moment from "moment"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const DOUBLE_BUTTON: ViewStyle = {
  width: "100%",
}

const MODAL_BUTTON: ViewStyle = {
  width: "45%",
  borderRadius: 40,
  borderColor: color.primary
}

const MODAL_BUTTON_NO: ViewStyle = {
  width: "45%",
  borderRadius: 40,
  borderColor: color.primary,
  backgroundColor: color.primary
}

const POWER_BUTTON: ViewStyle = {
  backgroundColor: color.primary,
  borderRadius: 70,
  padding: 30,
  borderWidth: 10,
  borderColor: color.palette.blue200
}

const MODAL: ViewStyle = {
  margin: 0,
  height: '100%',
  width: '100%',
  justifyContent: 'center',
  flexGrow: 1,
  backgroundColor: color.palette.white,
  paddingHorizontal: 16,
  paddingBottom: 20,
  alignItems: 'center'
}

const UNRECOGNIZED_IMAGE: ImageStyle = {
  // width: '100%',
  height: 170,
  resizeMode: 'contain',
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  backgroundColor: color.primary,
  alignItems: 'center',
  width: '60%',
  borderRadius: 5,
}

const rojaliSuccess = require("./success-mascout.png")

const SuccessModal: FC<{ colorScheme?: "light" | "dark", isOpen?: boolean, onNext?: () => void, disabled?: boolean }> = observer(function SuccessModal(props) {
  const { colorScheme, isOpen, onNext, disabled } = props
  return (
    <Modal isOpen={isOpen} onClose={onNext}>
      <SafeAreaView />
      <View
        style={MODAL}
        borderTopRadius="10px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <AutoImage source={rojaliSuccess} style={UNRECOGNIZED_IMAGE} />
        <Text
          tx="autoFeederPondCreateWizardScreen.successModalTitle"
          preset="title1"
          color={color.palette.gray900}
          colorScheme={colorScheme}
          marginBottom="8px"
          textAlign="center"
        />
        <Text
          tx="autoFeederPondCreateWizardScreen.successModalSubtitle"
          transformText={_.upperFirst}
          preset="body2"
          color={color.palette.gray800}
          colorScheme={colorScheme}
          marginBottom="40px"
          textAlign="center"
        />
        <Button
          testID="jala:id/next-button-modal"
          text={translate("common.next")}
          colorScheme={colorScheme}
          preset="primary"
          textColor={color.palette.white}
          style={BUTTON_DETAIL_ITEM}
          onPress={onNext}
          disabled={disabled}
        />
      </View>
    </Modal>
  )
})

const ConfirmationModal: FC<{ colorScheme?: "light" | "dark", isOpen?: boolean, onClose?: () => void, onConfirm?: () => void }> = observer(function ConfirmationModal(props) {
  const { colorScheme, isOpen, onClose, onConfirm } = props
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        alignSelf="stretch"
        marginY="80px"
        marginX="28px"
        paddingY="24px"
        paddingX="16px"
        rounded="16px"
        backgroundColor={nativeBaseTheme.colors.light["50"]}
      >
        <Text
          tx="autoFeederPondCreateWizardScreen.testRunModalTitle"
          preset="title1"
          colorScheme={colorScheme}
          marginBottom="20px"
          textAlign="center"
          color={color.palette.gray800}
        />
        <View flexDirection="row" alignItems="center" padding="16px" justifyContent="space-between">
          <Button
            testID="jala:id/left-button"
            text={translate("Yes").toUpperCase()}
            colorScheme={colorScheme}
            onPress={onConfirm}
            style={MODAL_BUTTON}
            preset="outline"
            textColor={color.palette.gray700}
          />
          <Button
            testID="jala:id/right-button"
            text={translate("No").toUpperCase()}
            colorScheme={colorScheme}
            onPress={onClose}
            style={MODAL_BUTTON_NO}
            textColor={color.palette.white}
          />
        </View>
      </View>
    </Modal>
  )
})

export const WizardTestRunScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function WizardTestRunScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { isOpen, onClose, onOpen } = useDisclose()

  const [isShowModal, setIsShowModal] = useState<boolean>(false)
  const [isDisabledTest, setIsDisabledTest] = useState<boolean>(false)

  const { appStore: { farmStore }, autoFeederPondStore: { state, loading } , autoFeederPondStore: modelStore } = useStores()
  const { autoFeederPond: model, setAutoFeederPond: setModel } = useAutoFeederPond()

  const initialValues = useMemo<any>(() => {
    return { ...(model && model.toArray()) }
  }, [model])

  // Pull in one of our MST stores
  // const { someStore, anotherStore } = useStores()

  const onTestRun = useCallback(async (second) => {
    await model.commandTestRun(second)
    onOpen()
  }, [model, onOpen])

  const runTestOnTime = useCallback(async () => {
    setIsDisabledTest(true)
    try {
      await onTestRun(state?.onTime || 1);
    } catch (e) {
      await setIsDisabledTest(false)
      Toast.show({ title: e.message })
      if (e?.code === 'NOT_CURRENTLY_CONNECTED') {
        await model.connect("bluetooth")
        setModel(model)
        Toast.show({ title: "Please try again" })
      }
    }
    await setIsDisabledTest(false)
  }, [state?.onTime, onTestRun, model, setModel])

  // const handleSubmit = useCallback(() => {
  //   setIsShowModal(true)
  // }, [])

  const handleBack = useCallback(() => {
    goBack('Home.Stack')
  }, [])

  const onConfirmModal = useCallback(() => {
    onClose()
    setIsShowModal(true)
  }, [onClose])

  const onSuccess = useCallback(async () => {
    modelStore.set('loading', true)
    const success = await modelStore.update(model, {
      state: {
        ...state,
        asto_wizard_finish: true,
        updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
      }
    })
    if (success) {
      farmStore.set('pondId', initialValues.pond_id)
      farmStore.pondStore.set('autoFeederPondId', initialValues.id)
      await modelStore.set('needRefresh', true)
      await farmStore.pondStore.getAutoFeederPonds(true, async () => {
        modelStore.set('loading', false)
        setModel(AutoFeederPond.hydrate(farmStore.pondStore.autoFeederPond))
        await delay(1)
        navigate('Home.AutoFeederPond.Stack', 'Home.Farm.AutoFeederPond.List')
        navigate('Home.AutoFeederPond.Stack', 'Home.AutoFeederPond.Detail.Stack', { id: farmStore.pondStore.autoFeederPondId })
      })
    }
    setIsShowModal(false)
  }, [farmStore, initialValues, model, modelStore, setModel, state])

  const ConfirmationModalComponent = useCallback(() => {
    return (
      <ConfirmationModal isOpen={isOpen} onClose={onClose} onConfirm={onConfirmModal} />
    )
  }, [isOpen, onClose, onConfirmModal])

  const SuccessModalComponent = useCallback(() => (
    <SuccessModal isOpen={isShowModal} onNext={onSuccess} disabled={loading} />
  ), [isShowModal, loading, onSuccess])

  const holdText = isDisabledTest ? "Running..." : translate("autoFeederPondCreateWizardScreen.hold")

  return (
    <Screen style={ROOT} preset="fixed" colorScheme={colorScheme}>
      <SafeAreaView />
      <Text preset="body2" text={`4/4: ${translate("autoFeederPondCreateWizardScreen.testRunSetup")}`} color={color.palette.gray800} mb="8px" ml="16px" />
      <View height="3px" backgroundColor={color.palette.gray300} borderRadius="5px" mx="16px">
        <View height="3px" backgroundColor={color.primary} width="100%" borderRadius="5px" />
      </View>
      <View flex={1} px="16px">
        <Text preset="title3" tx="autoFeederPondEditScreen.testRun" color={color.palette.gray900} mt="30px" />
        <Text preset="body2" tx="autoFeederPondCreateWizardScreen.testRunSubtitle" color={color.palette.gray700} mt="4px" mb="12px" />
        <View py="8px" backgroundColor={color.palette.orange300} flexDirection="row" alignItems="center" mx="-16px" px="16px">
          <JalaIcon name="info-1" size={16} color={color.palette.gray800} />
          <Text tx="autoFeederPondCreateWizardScreen.infoTestRun" color={color.palette.gray700} preset="body3" ml="5px" />
          <Text text={translate("autoFeederPondCreateWizardScreen.infoTestRunOnTime", { onTime: state.onTime })} color={color.palette.gray700} preset="title5" />
        </View>
        <View alignItems="center" mt="110px">
          <TouchableOpacity style={POWER_BUTTON} disabled={isDisabledTest} onPress={runTestOnTime}>
            <MaterialIcons name="power-settings-new" size={50} color={color.palette.white} />
          </TouchableOpacity>
          <Text text={holdText} colorScheme={colorScheme} preset="body2" mt="20px" />
          <ConfirmationModalComponent />
        </View>
        <SuccessModalComponent />
      </View>
      <View flexDirection="row" alignItems="center" padding="16px" justifyContent="space-between" borderTopWidth={1} borderColor={color.palette.gray400}>
        <Button
          testID="jala:id/left-button"
          tx="common.back"
          colorScheme={colorScheme}
          // loading={loading}
          onPress={handleBack}
          style={DOUBLE_BUTTON}
          preset="outline"
        />
      </View>
    </Screen>
  )
})

